import { ChangeDetectionStrategy, Component, Injector, computed, inject, signal, type Signal } from '@angular/core';
import { BrnDialogRef, injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { CopyBoxComponent } from '@lite-workspace/shared/blocks/copy-box';
import { DialogSheetComponent } from '@lite-workspace/shared/helm-ui/dialog';
import { HlmTooltip } from '@lite-workspace/shared/helm-ui/tooltip';
import { IconComponent } from '@lite-workspace/shared/blocks/icon';
import { SupportChannelMenuService } from '@lite-workspace/shared/onboarding-service';
import { UPLOAD_SUPPORT_CONTACT } from './upload-support.token';

export interface FailedUploadItem {
  readonly name: string;
  readonly reason: string;
  /** Absent = retryable. */
  readonly retryable?: boolean;
  /** blob:/http address of the picked file — the row shows the frame when the caller has one. */
  readonly previewUrl?: string;
}

export interface FailedUploadsDialogContext {
  /** Live: the window opens at the first failure, later ones join it while it is open. */
  readonly items: Signal<readonly FailedUploadItem[]>;
  /** True = «Повторить загрузку» already ran on these files and failed again: support takes the copy button's place. */
  readonly repeated?: Signal<boolean>;
}

/** Lumir-lab parity: the failed-uploads summary — count, the reason, file names on demand, retry. */
@Component({
  selector: 'app-failed-uploads-dialog',
  standalone: true,
  imports: [HlmButton, CopyBoxComponent, DialogSheetComponent, IconComponent, HlmTooltip],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class:
      'block dlg-phone:contents dlg-desktop:w-[var(--dlg-w-project)] dlg-desktop:rounded-[var(--r-xl)] ' +
      'dlg-desktop:border dlg-desktop:border-line2 dlg-desktop:bg-panel dlg-desktop:p-8 ' +
      'dlg-desktop:shadow-[var(--shadow-4)]',
  },
  template: `
    <hlm-dialog-sheet>
      <ng-template>
        <h2 class="mb-1.5 flex items-center gap-2.5 text-lg font-bold text-ink">
          <app-icon name="circle-alert" [size]="22" class="shrink-0 text-bad" />
          {{ title() }}
        </h2>
        <!-- Одна причина на всё окно, когда она у всех файлов одна; разные причины — у каждого имени в списке. -->
        @if (sharedReason(); as reason) {
          <p class="whitespace-pre-line text-base leading-normal text-c2">{{ reason }}</p>
        }

        @if (items().length === 1) {
          <p class="mt-4 truncate text-sm text-ink" [hlmTooltip]="items()[0].name">{{ items()[0].name }}</p>
        } @else if (namesOpen()) {
          <ul
            class="mt-4 max-h-33 overflow-y-auto rounded-[var(--r-s)] border border-line2 bg-bg2
              px-3.5 py-2.5 text-sm leading-5.5"
          >
            @for (item of items(); track item.name) {
              @if (sharedReason()) {
                <li class="truncate text-ink">{{ item.name }}</li>
              } @else {
                <!-- Причина — под именем и переносится: длинный отказ не должен выдавить имя файла. -->
                <li class="flex items-start gap-2.5 py-1">
                  @if (item.previewUrl) {
                    <img [src]="item.previewUrl" [alt]="item.name" class="size-10 shrink-0 rounded-[var(--r-s)] object-cover" />
                  }
                  <div class="min-w-0 flex-1">
                    <span class="block truncate text-ink" [hlmTooltip]="item.name">{{ item.name }}</span>
                    <span class="block whitespace-pre-line text-xs text-c2">{{ item.reason }}</span>
                  </div>
                </li>
              }
            }
          </ul>
        } @else {
          <button
            hlmBtn
            type="button"
            variant="link"
            class="mt-4 h-auto p-0 text-sm underline underline-offset-2"
            (click)="namesOpen.set(true)"
          >
            Показать имена файлов
          </button>
        }

        <div class="mt-6 flex gap-3">
          <button hlmBtn type="button" variant="outline" class="h-12 w-16 flex-none justify-center" (click)="close()">ОК</button>
          <!-- Повтор упал тем же сбоем — копировать имена уже незачем, нужен живой человек. -->
          @if (repeated()) {
            <button hlmBtn type="button" variant="outline" class="h-12 flex-auto justify-center" (click)="contactSupport()">
              Написать в поддержку
            </button>
          } @else {
            <app-copy-box
              appearance="button"
              buttonVariant="outline"
              copyLabel="Скопировать имена"
              class="flex-auto [&>button]:h-12 [&>button]:w-full [&>button]:justify-center"
              [value]="names()"
              [valueInLabel]="false"
            />
          }
          @if (hasRetryable()) {
            <button hlmBtn type="button" class="h-12 flex-auto justify-center" (click)="retryAll()">Повторить загрузку</button>
          }
        </div>
      </ng-template>
    </hlm-dialog-sheet>
  `,
})
export class FailedUploadsDialogComponent {
  protected readonly ctx = injectBrnDialogContext<FailedUploadsDialogContext>();
  private readonly dialogRef = inject(BrnDialogRef);
  private readonly injector = inject(Injector);
  private readonly support = inject(UPLOAD_SUPPORT_CONTACT, { optional: true });
  // Кнопка повтора есть, только когда есть что повторять: окончательный отказ
  // (дубль, тип, размер) повторяется тем же отказом и зациклил бы фотографа.
  protected readonly items = this.ctx.items;
  protected readonly namesOpen = signal(false);
  protected readonly repeated = computed(() => this.ctx.repeated?.() ?? false);
  protected readonly hasRetryable = computed(() => this.items().some(item => item.retryable !== false));
  // «фото» не склоняется: одна форма на любое число, у одного файла числа нет вовсе.
  protected readonly title = computed(() =>
    this.items().length === 1 ? 'Не удалось загрузить фото' : `Не удалось загрузить ${this.items().length} фото`,
  );
  protected readonly sharedReason = computed(() => {
    const [first, ...rest] = this.items();
    return first && rest.every(item => item.reason === first.reason) ? first.reason : null;
  });
  // Через запятую, а не строками: однострочное поле, куда их вставляют, склеило бы имена (D9).
  protected readonly names = computed(() =>
    this.items()
      .map(item => item.name)
      .join(', '),
  );

  protected retryAll(): void {
    this.dialogRef.close(true);
  }

  protected close(): void {
    this.dialogRef.close(false);
  }

  /** Synchronous on the click: the channel opener needs the gesture for its tab. */
  protected contactSupport(): void {
    if (this.support) this.support();
    else this.injector.get(SupportChannelMenuService).open();
  }
}
